import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';

import { SubSink } from 'subsink';

import { AvailableZonesGQL, BaseZoneFragment, CalendarEventForShareFragment, EditCalendarEventGQL, EditCalendarEventMutationVariables, ShareEventZoneJobEventsGQL } from '../../../generated/graphql.generated';
import { ZONE_FILTER_THRESHOLD } from '../../global.constants';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

@Component({
  selector: 'app-share-event-zone',
  templateUrl: './share-event-zone.component.html',
  styleUrls: ['./share-event-zone.component.scss']
})
export class ShareEventZoneComponent implements OnInit, OnDestroy {
  zonesInitialized = false;
  eventsInitialized = false;

  /** All non-cancelled events on the job. */
  events: CalendarEventForShareFragment[] = [];

  subs = new SubSink();

  availableZones: BaseZoneFragment[];

  shareEventForm = new UntypedFormGroup({
    zone: new UntypedFormControl(null),
    selectedEvents: new UntypedFormControl([]),
  });

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private editCalendarEventGQL: EditCalendarEventGQL,
    private localNotify: FreyaNotificationsService,
    private availableZonesGQL: AvailableZonesGQL,
    private detailsHelper: DetailsHelperService,
    private shareEventZoneJobEventsGQL: ShareEventZoneJobEventsGQL,
  ) { }

  ngOnInit(): void {
    const jobId: string = this.config.data.jobId;
    const preSelectedEventId: string | undefined = this.config.data.preSelectedEventId;

    this.subs.sink = forkJoin({
      eventsRes: this.shareEventZoneJobEventsGQL.fetch({ jobId }),
      zonesRes: this.availableZonesGQL.fetch({ limit: -1 }),
    }).subscribe({
      next: ({ eventsRes, zonesRes }) => {
        // 1. Populate events (cancelled already excluded by query filter)
        this.events = eventsRes.data.calendarEvents.events ?? [];

        // 2. Pre-select the clicked event
        if (preSelectedEventId) {
          this.shareEventForm.controls.selectedEvents.setValue(
            this.events.filter((e) => e.id === preSelectedEventId)
          );
        }

        // 3. Populate zones — seed from the pre-selected event, fall back to all job events
        this.availableZones = (zonesRes.data.availableZones ?? []).filter((z) => z.type === 'franchise');

        const seedEvent = this.events.find((e) => e.id === preSelectedEventId);
        const seedZoneId = seedEvent?.sharedZones?.[0]
          ?? this.events.flatMap((e) => e.sharedZones ?? [])[0];

        this.shareEventForm.controls.zone.setValue(
          this.availableZones.find((z) => z.id === seedZoneId) ?? null
        );

        // 4. Mark ready in one place
        this.eventsInitialized = true;
        this.zonesInitialized = true;
      },
      error: (err) => {
        this.localNotify.apolloError('Failed to load share dialog', err);
        this.eventsInitialized = true;
        this.zonesInitialized = true;
      },
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  closeDialog() {
    this.ref.close();
  }

  get isReady(): boolean {
    return this.zonesInitialized && this.eventsInitialized;
  }

  get isSharingWholeJob(): boolean {
    return (this.shareEventForm.value.selectedEvents as CalendarEventForShareFragment[]).length === 0;
  }

  get isUnsharing(): boolean {
    const zone = this.shareEventForm.value.zone as BaseZoneFragment | null;
    return this.isReady && !zone;
  }

  get showFilter(): boolean {
    return (this.availableZones?.length ?? 0) > ZONE_FILTER_THRESHOLD;
  }

  shareEvent() {
    if (!this.isReady) return;
    const selectedEvents: CalendarEventForShareFragment[] = this.shareEventForm.value.selectedEvents;
    // When no specific events are chosen, share all events on the job.
    const eventIds = selectedEvents.length > 0
      ? selectedEvents.map((e) => e.id)
      : this.events.map((e) => e.id);

    const zone = this.shareEventForm.value.zone as BaseZoneFragment | null;
    const input: EditCalendarEventMutationVariables = {
      ids: eventIds,
      edit: {
        setSharedZone: zone?.id ?? null,
      },
    };

    this.editCalendarEventGQL.mutate(input).subscribe({
      next: () => {
        const action = this.isUnsharing ? 'unshared' : 'shared';
        const label = selectedEvents.length > 0
          ? `${selectedEvents.length} event${selectedEvents.length > 1 ? 's' : ''} ${action}`
          : `Job ${action}`;
        this.localNotify.success(label);
        eventIds.forEach((id) => {
          this.detailsHelper.pushUpdate({ action: 'update', id, type: 'Events' });
        });
        this.closeDialog();
      },
      error: (err) => {
        this.localNotify.apolloError('Failed to share', err);
      },
    });
  }

}
